import React, { useEffect, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { calendarService } from '../../services/calendar.service';
import type { CalendarEvent, CalendarEventFormData } from '../../types/calendar';
import { useNotification } from '../../contexts/NotificationContext';

interface EventFormProps {
  eventData?: CalendarEvent | null; // For editing existing event
  selectedDate?: Date | null; // For pre-filling date when adding new event
  onSuccess: () => void;
  onCancel: () => void;
}

export const EventForm: React.FC<EventFormProps> = ({ eventData, selectedDate, onSuccess, onCancel }) => {
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<CalendarEventFormData>();
  const [isFormLoading, setIsFormLoading] = useState(false); // New state for form loading
  const { addNotification } = useNotification();

  useEffect(() => {
    if (eventData) {
      // When editing, populate form with existing event data
      reset({
        title: eventData.title,
        start: eventData.start.split('T')[0], // Date part only for date input
        end: eventData.end ? eventData.end.split('T')[0] : '',
        allDay: eventData.allDay,
        description: eventData.description,
      });
    } else if (selectedDate) {
      // When adding, pre-fill start date
      reset({
        title: '',
        start: selectedDate.toISOString().split('T')[0],
        end: '',
        allDay: true, // Default to all-day
        description: '',
      });
    } else {
      // Default for new event without selected date
      reset({
        title: '',
        start: new Date().toISOString().split('T')[0],
        end: '',
        allDay: true,
        description: '',
      });
    }
  }, [eventData, selectedDate, reset]);

  const onSubmit: SubmitHandler<CalendarEventFormData> = async (data) => {
    setIsFormLoading(true); // Use new state for form loading
    try {
      if (eventData) {
        await calendarService.updateEvent(eventData.id, data);
        addNotification('이벤트가 성공적으로 수정되었습니다.', 'success');
      } else {
        await calendarService.addEvent(data);
        addNotification('새 이벤트가 성공적으로 추가되었습니다.', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving event:", error);
      addNotification('이벤트 저장 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsFormLoading(false); // Use new state for form loading
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
      <div>
        <label htmlFor="title" className="block text-base font-normal text-gray-600">제목</label>
        <input id="title" {...register('title', { required: '제목은 필수 항목입니다.' })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm" />
        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="start" className="block text-base font-normal text-gray-600">시작일</label>
          <input type="date" id="start" {...register('start', { required: '시작일은 필수 항목입니다.' })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600" />
          {errors.start && <p className="mt-1 text-sm text-red-600">{errors.start.message}</p>}
        </div>
        <div>
          <label htmlFor="end" className="block text-base font-normal text-gray-600">종료일 (선택 사항)</label>
          <input type="date" id="end" {...register('end')} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600" />
        </div>
      </div>

      <div className="flex items-center">
        <input type="checkbox" id="allDay" {...register('allDay')} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
        <label htmlFor="allDay" className="ml-2 block text-base font-normal text-gray-600">하루 종일</label>
      </div>

      <div className="flex items-center">
        <input type="checkbox" id="receiveNotification" {...register('receiveNotification')} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
        <label htmlFor="receiveNotification" className="ml-2 block text-base font-normal text-gray-600">알림 받기 (스마트폰 푸시 알림 - 기능 예정)</label>
      </div>

      <div>
        <label htmlFor="description" className="block text-base font-normal text-gray-600">설명 (선택 사항)</label>
        <textarea id="description" {...register('description')} rows={3} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm"></textarea>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button type="button" onClick={onCancel} className="px-4 py-1.5 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow-md hover:bg-gray-300 transition duration-150 ease-in-out">
          취소
        </button>
        <button type="submit" disabled={isFormLoading || isSubmitting} className="px-4 py-1.5 bg-btn-green-bg hover:bg-btn-green-hover text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out disabled:opacity-50">
          {isFormLoading ? '저장 중...' : (eventData ? '이벤트 수정' : '이벤트 추가')}
        </button>
      </div>
    </form>
  );
};
